import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAppStore } from "../../store";
import { api } from "../../api/client";

interface Props {
  children: React.ReactNode;
  fallback: React.ReactNode;
}

export function AuthGuard({ children, fallback }: Props) {
  const [loading, setLoading] = useState(true);
  const { setAuth, clearAuth, isAuthenticated } = useAppStore();

  useEffect(() => {
    // If Supabase not configured (local dev), treat as authenticated
    if (!supabase) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setAuth(session.access_token, session.user.id);
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        api.preferences.update({ timezone: tz }).catch(() => {});
      } else {
        clearAuth();
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setAuth(session.access_token, session.user.id);
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        api.preferences.update({ timezone: tz }).catch(() => {});
      } else {
        clearAuth();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[var(--color-bg,#0a0a0a)]">
        <div className="text-[var(--color-text-muted,#666)] text-sm">Loading...</div>
      </div>
    );
  }

  // Local dev (no Supabase) OR authenticated: show app
  if (!supabase || isAuthenticated) return <>{children}</>;

  // Has Supabase but not authenticated: show fallback (login page)
  return <>{fallback}</>;
}
